import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Text, StatusIndicator, Button, cn, EmptyState } from '@chatsdet/ui';
import { CaretDown, CaretUp } from '@phosphor-icons/react';

export type StatusVariant = 'positive' | 'negative' | 'notice' | 'info' | 'neutral';

export type FormattedEvent = {
  text: string;
  status: StatusVariant;
  statusLabel?: string;
  detail?: string;
  timestamp?: number; // Unix timestamp in ms
};

type AnimatedEventStreamProps<T> = {
  events: T[];
  formatEvent: (event: T) => FormattedEvent;
  renderStatus?: (event: T, isLatest: boolean) => React.ReactNode;
  onEventClick?: (event: T) => void;
  onEventChange?: (event: T) => void;
  getEventKey?: (event: T, index: number) => string;
  className?: string;
  maxVisible?: number;
};

function formatTimestamp(ts: number): string {
  const date = new Date(ts);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

/**
 * Jump input with out-of-range warning feedback
 */
function JumpInput({
  jumpValue,
  setJumpValue,
  totalCount,
  currentPosition,
  onJump,
  onCancel,
}: {
  jumpValue: string;
  setJumpValue: (value: string) => void;
  totalCount: number;
  currentPosition: number;
  onJump: (eventNumber: number) => void;
  onCancel: () => void;
}) {
  const parsedValue = useMemo(() => {
    if (jumpValue === '') return null;
    const num = parseInt(jumpValue, 10);
    return Number.isFinite(num) ? num : null;
  }, [jumpValue]);

  const isOutOfRange = parsedValue !== null && (parsedValue < 1 || parsedValue > totalCount);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (parsedValue !== null) {
          onJump(parsedValue);
        }
      }}
      className="flex items-center gap-1"
    >
      <input
        type="text"
        inputMode="numeric"
        value={jumpValue}
        onChange={(e) => {
          // Allow only digits
          if (/^\d*$/.test(e.target.value)) {
            setJumpValue(e.target.value);
          }
        }}
        placeholder={String(currentPosition)}
        autoFocus
        onBlur={onCancel}
        className={cn(
          'w-14 h-6 text-xs text-center bg-muted border rounded px-1',
          isOutOfRange ? 'border-warning' : 'border-border'
        )}
      />
      <Text size="xs" className="text-muted-foreground">
        / {totalCount}
      </Text>
    </form>
  );
}

export function AnimatedEventStream<T>({
  events,
  formatEvent,
  renderStatus,
  onEventClick,
  onEventChange,
  getEventKey,
  className = '',
  maxVisible = 3,
}: AnimatedEventStreamProps<T>) {
  const [hasManuallyNavigated, setHasManuallyNavigated] = useState(false);
  const [navigationDirection, setNavigationDirection] = useState<'forward' | 'backward'>('forward');
  const [, forceRender] = useState(0);
  const [showJumpInput, setShowJumpInput] = useState(false);
  const [jumpValue, setJumpValue] = useState('');

  // Track which event we're viewing by its original array index (null = at front)
  const viewingEventOriginalIndexRef = useRef<number | null>(null);
  const eventsCountAtNavigationRef = useRef(events.length);
  const prevEventsLengthRef = useRef(events.length);
  const lastNotifiedEventKeyRef = useRef<string | null>(null);
  const isFirstRenderRef = useRef(true);

  // Compute currentIndex synchronously from the event we're viewing
  const currentIndex =
    viewingEventOriginalIndexRef.current === null
      ? 0
      : Math.max(0, events.length - 1 - viewingEventOriginalIndexRef.current);

  // Calculate new events count (events added since user navigated away)
  const newEventsCount = hasManuallyNavigated
    ? Math.max(0, events.length - eventsCountAtNavigationRef.current)
    : 0;

  // Calculate visible events from current position
  const reversedEvents = events.slice().reverse();
  const effectiveMaxVisible = Math.max(1, maxVisible);
  const visibleEvents = reversedEvents.slice(currentIndex, currentIndex + effectiveMaxVisible);

  useEffect(() => {
    isFirstRenderRef.current = false;
  }, []);

  // Navigation handlers
  // Previous = go to older events (increase index)
  const handlePrevious = () => {
    if (currentIndex < reversedEvents.length - 1) {
      const newIndex = currentIndex + 1;
      const targetEvent = reversedEvents[newIndex];
      const targetOriginalIndex = events.length - 1 - newIndex;

      viewingEventOriginalIndexRef.current = targetOriginalIndex;
      if (!hasManuallyNavigated) {
        setHasManuallyNavigated(true);
        eventsCountAtNavigationRef.current = events.length;
      }
      setNavigationDirection('forward');
      forceRender((value) => value + 1);
      if (onEventChange && targetEvent) {
        onEventChange(targetEvent);
      }
    }
  };

  // Next = go to newer events (decrease index)
  const handleNext = () => {
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;

      if (newIndex === 0) {
        // Going back to front
        viewingEventOriginalIndexRef.current = null;
        setHasManuallyNavigated(false);
        eventsCountAtNavigationRef.current = events.length;
      } else {
        const targetOriginalIndex = events.length - 1 - newIndex;
        viewingEventOriginalIndexRef.current = targetOriginalIndex;

        if (!hasManuallyNavigated) {
          setHasManuallyNavigated(true);
          eventsCountAtNavigationRef.current = events.length;
        }
      }

      setNavigationDirection('backward');
      forceRender((value) => value + 1);
      if (onEventChange && reversedEvents[newIndex]) {
        onEventChange(reversedEvents[newIndex]);
      }
    }
  };

  const handleJumpToFront = () => {
    viewingEventOriginalIndexRef.current = null;
    setHasManuallyNavigated(false);
    eventsCountAtNavigationRef.current = events.length;
    setNavigationDirection('backward');
    forceRender((value) => value + 1);
    if (onEventChange && reversedEvents[0]) {
      onEventChange(reversedEvents[0]);
    }
  };

  // Jump to specific event number
  const handleJumpToEvent = (eventNumber: number) => {
    const clampedNumber = Math.max(1, Math.min(eventNumber, totalCount));
    const newIndex = totalCount - clampedNumber; // Convert event # to reversed index

    if (newIndex === 0) {
      viewingEventOriginalIndexRef.current = null;
      setHasManuallyNavigated(false);
    } else {
      const targetOriginalIndex = events.length - 1 - newIndex;
      viewingEventOriginalIndexRef.current = targetOriginalIndex;
      setHasManuallyNavigated(true);
    }
    eventsCountAtNavigationRef.current = events.length;
    setNavigationDirection(newIndex < currentIndex ? 'backward' : 'forward');
    forceRender((value) => value + 1);
    setShowJumpInput(false);
    setJumpValue('');
    if (onEventChange && reversedEvents[newIndex]) {
      onEventChange(reversedEvents[newIndex]);
    }
  };

  useEffect(() => {
    const prevLength = prevEventsLengthRef.current;
    const nextLength = events.length;

    // Clamp viewed index if source list shrinks
    if (viewingEventOriginalIndexRef.current !== null) {
      if (nextLength === 0) {
        viewingEventOriginalIndexRef.current = null;
      } else {
        viewingEventOriginalIndexRef.current = Math.min(
          viewingEventOriginalIndexRef.current,
          nextLength - 1
        );
      }
    }

    // Update unseen counter references
    if (!hasManuallyNavigated || currentIndex === 0) {
      eventsCountAtNavigationRef.current = nextLength;
    } else if (nextLength < eventsCountAtNavigationRef.current) {
      eventsCountAtNavigationRef.current = nextLength;
    }

    // Detect new events while at front to animate correctly
    if (!hasManuallyNavigated && nextLength > prevLength) {
      setNavigationDirection('backward');
    }

    if (nextLength === 0) {
      lastNotifiedEventKeyRef.current = null;
    }

    prevEventsLengthRef.current = nextLength;
  }, [events.length, hasManuallyNavigated, currentIndex]);

  useEffect(() => {
    if (!onEventChange || reversedEvents.length === 0) {
      return;
    }

    const activeEvent = reversedEvents[currentIndex];
    if (!activeEvent) {
      return;
    }

    const originalIndex = events.indexOf(activeEvent);
    const key = getEventKey ? getEventKey(activeEvent, originalIndex) : `event-${originalIndex}`;

    if (key !== lastNotifiedEventKeyRef.current) {
      lastNotifiedEventKeyRef.current = key;
      onEventChange(activeEvent);
    }
  }, [currentIndex, events, getEventKey, onEventChange, reversedEvents]);

  const totalCount = reversedEvents.length;
  const canGoPrevious = currentIndex < totalCount - 1;
  const canGoNext = currentIndex > 0;
  const isAtFront = currentIndex === 0;
  const currentPosition = totalCount > 0 ? totalCount - currentIndex : 0;

  return (
    <div className={`flex flex-col gap-4 ${className}`}>
      {events.length === 0 ? (
        <EmptyState size="sm" title="No events yet" />
      ) : (
        <>
          <nav className="flex items-center justify-between gap-3">
            {!isAtFront && newEventsCount > 0 ? (
              <Button variant="outline" size="sm" onPress={handleJumpToFront} className="gap-1">
                Jump ({newEventsCount})
              </Button>
            ) : (
              <Button variant="ghost" size="sm" isDisabled className="text-muted-foreground">
                Newest
              </Button>
            )}
            <div className="flex items-center gap-1">
              {/* Position indicator - clickable to jump */}
              {showJumpInput ? (
                <JumpInput
                  jumpValue={jumpValue}
                  setJumpValue={setJumpValue}
                  totalCount={totalCount}
                  currentPosition={currentPosition}
                  onJump={handleJumpToEvent}
                  onCancel={() => {
                    setShowJumpInput(false);
                    setJumpValue('');
                  }}
                />
              ) : (
                <button
                  onClick={() => setShowJumpInput(true)}
                  className="text-xs text-muted-foreground hover:text-foreground px-2 py-1 rounded hover:bg-muted transition-colors"
                  title="Click to jump to event #"
                >
                  {currentPosition} / {totalCount}
                </button>
              )}
              <Button
                variant="icon"
                size="sm"
                onPress={handlePrevious}
                isDisabled={!canGoPrevious}
                iconOnly
                tooltip="Older event"
                className="text-muted-foreground"
              >
                <CaretDown size={16} weight="bold" />
              </Button>
              <Button
                variant="icon"
                size="sm"
                onPress={handleNext}
                isDisabled={!canGoNext}
                iconOnly
                tooltip="Newer event"
                className="text-muted-foreground"
              >
                <CaretUp size={16} weight="bold" />
              </Button>
            </div>
          </nav>

          <div className="relative h-64">
            <AnimatePresence mode="popLayout">
              {visibleEvents.map((event, index) => {
                const originalIndex = events.indexOf(event);
                const isLatest = index === 0;
                const depth = index;
                const scale = Math.max(0.82, 1 - depth * 0.08);
                const yOffset = depth * 22;
                const opacity = depth === 0 ? 1 : Math.max(0.35, 1 - depth * 0.22);
                const blur = depth === 0 ? 0 : Math.min(8, depth * 4);
                const formatted = formatEvent(event);
                const key = getEventKey
                  ? getEventKey(event, originalIndex)
                  : `event-${originalIndex}`;
                const shouldAnimateEntrance = isAtFront && !isFirstRenderRef.current;

                return (
                  <motion.div
                    key={key}
                    layout
                    initial={
                      shouldAnimateEntrance
                        ? {
                            y: navigationDirection === 'backward' ? -48 : 48,
                            opacity: 0,
                            scale: navigationDirection === 'backward' ? 1.05 : 0.94,
                            filter: `blur(${blur}px)`,
                          }
                        : false
                    }
                    animate={{
                      y: yOffset,
                      opacity,
                      scale,
                      zIndex: effectiveMaxVisible - depth,
                      filter: `blur(${blur}px)`,
                      transition: {
                        type: 'spring',
                        stiffness: 320,
                        damping: 28,
                        mass: 0.6,
                      },
                    }}
                    exit={{
                      y: navigationDirection === 'forward' ? -110 : 110,
                      opacity: 0,
                      scale: navigationDirection === 'forward' ? 1.02 : 0.9,
                      filter: 'blur(6px)',
                      transition: {
                        duration: 0.28,
                        ease: [0.4, 0, 0.2, 1],
                      },
                    }}
                    className="absolute top-0 left-0 right-0"
                    style={{
                      transformOrigin: 'top center',
                      pointerEvents: isLatest ? 'auto' : 'none',
                    }}
                  >
                    <div
                      className={`
                        bg-background border rounded-lg shadow-sm p-4
                        ${isLatest ? 'border-primary shadow-md' : 'border-border'}
                        ${onEventClick ? 'cursor-pointer hover:border-primary/50' : ''}
                      `}
                      onClick={() => onEventClick?.(event)}
                    >
                      <div className="flex justify-between items-center mb-2">
                        {formatted.timestamp ? (
                          <Text size="xs" className="text-muted-foreground/25 font-mono">
                            {formatTimestamp(formatted.timestamp)}
                          </Text>
                        ) : (
                          <div />
                        )}
                        <StatusIndicator variant={formatted.status} className="text-xs">
                          {formatted.statusLabel || formatted.status}
                        </StatusIndicator>
                      </div>
                      <div className="w-full">
                        <div className="flex items-start gap-2">
                          <Text
                            size="sm"
                            className="leading-relaxed break-words break-all overflow-wrap-anywhere flex-1"
                          >
                            {formatted.text}
                          </Text>
                          {renderStatus && (
                            <div className="flex-shrink-0">{renderStatus(event, isLatest)}</div>
                          )}
                        </div>
                        {formatted.detail && (
                          <Text
                            size="xs"
                            className="text-muted-foreground mt-1 italic break-words break-all overflow-wrap-anywhere"
                          >
                            {formatted.detail}
                          </Text>
                        )}
                      </div>
                      {isLatest && (
                        <motion.div
                          initial={{ scaleX: 0 }}
                          animate={{ scaleX: 1 }}
                          className="h-0.5 bg-primary origin-left mt-3"
                          transition={{ duration: 0.5 }}
                        />
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>

          {/* Navigation Controls */}
          <div className="flex flex-col items-center gap-3"></div>
        </>
      )}
    </div>
  );
}
